import React, { useMemo } from "react";
import {
  useOne,
  useTranslate,
} from "@refinedev/core";
import {
  Typography,
  Stack,
  Paper,
  Divider,
} from "@mui/material";
import { IProduct } from "@/utils/zod/productCollectionFormValidation";
import ProductImageCarousel from "./ProductImageCarousal";
import { ShowField } from "../show/Field";

interface ProductShowContentProps {
  product: IProduct;
}

export const ProductShowContent: React.FC<ProductShowContentProps> = ({ product }) => {
  const t = useTranslate();

  const medias = useMemo(() => product.medias, [product.medias]);

  const { data: pdfMedia } = useOne({
    resource: "medias",
    id: product.pdf_catalog_media_id || "",
    queryOptions: {
      enabled: !!product.pdf_catalog_media_id,
    }
  });

  return (
    <Stack spacing="32px" padding="32px 32px 56px 32px">
      {/* Product Images */}
      <ProductImageCarousel medias={medias} />

      {/* Product Info */}
      <Stack spacing="24px">
        <Typography variant="h5">{product.name}</Typography>

        <Paper>

          {/* PDF Catalog */}
          <Stack
            direction="row"
            alignItems="center"
            spacing="8px"
          >
              <Typography fontWeight="600">Catalog PDF</Typography>
              <iframe src={pdfMedia?.data.cdn_url} width="100%" height="500px" />
          </Stack>

          <Divider />

          {/* name */}
          <ShowField title={t("products.fields.name")} value={product.name} />

          {/* SKU ID */}
          <ShowField title={t("products.fields.sku_id")} value={product.sku_id} />

          {/* available_quantity */}
          <ShowField title={t("products.fields.available_quantity")} value={product.available_quantity.toString()} />

          {/* UOM */}
          <ShowField title={t("products.fields.uom")} value={product.uom || ""} />

          {/* minimum_order_quantity */}
          <ShowField title={t("products.fields.minimum_order_quantity")} value={product.minimum_order_quantity?.toString() || "1"} />

          {/* Price */}
          <ShowField title={t("products.fields.price")} value={product.cost_price} />

          {/* Features */}
          <ShowField title={t("products.fields.features")} value={product.features?.join(", ") || ""} />

          {/* Material */}
          <ShowField title={t("products.fields.material")} value={product.material || ""} />

          {/* Description */}
          <ShowField title={t("products.fields.description")} value={product.description || ""} />

          {/* Status */}
          <ShowField title={t("products.fields.status.label")} value={[t(`products.fields.status.${product.status}`)]} chipStatus={product.status === "active" ? "positive" : "neutral"} chipType="status" />

          {/* Collections */}
          <ShowField title={t("products.fields.collections")} value={product.collections?.map((collection) => collection.name) || []} chipType="collection" />

          {/* Deal Name */}
          <ShowField title={t("products.fields.deal_name")} value={product.deal_name || ""} />

          {/* Rating Stars */}
          <ShowField title={t("products.fields.rating_stars")} value={product.rating_stars?.toString() || ""} />

          {/* Review Count */}
          <ShowField title={t("products.fields.review_count")} value={product.review_count?.toString() || ""} />

          {/* Created At */}
          <ShowField title={t("products.fields.createdAt")} value={product.inserted_at?.toString() || ""} chipType="date" />
        </Paper>
      </Stack>
    </Stack>
  );
};

export default ProductShowContent; 